import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '@/lib/api';

const PharmacyModeContext = createContext(null);

export function PharmacyModeProvider({ children }) {
  const { user } = useAuth();
  const [mode, setMode] = useState('manual'); // 'connected' | 'manual'
  const [connections, setConnections] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [primaryCompany, setPrimaryCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const isPharmacy = user?.workspace_type === 'pharmacy' ||
    ['pharmacy_owner', 'pharmacy_admin'].includes(user?.role);

  const fetchMode = useCallback(async () => {
    if (!isPharmacy) {
      setMode('manual');
      setConnections([]);
      setRestrictions([]);
      setPrimaryCompany(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/pharmacy/mode');
      const d = res.data;
      setMode(d.mode);
      setConnections(d.connections || []);
      setRestrictions(d.restrictions || []);
      setPrimaryCompany(d.primary_company || null);
    } catch (e) {
      /* silent */
      setMode('manual');
    }
    setLoading(false);
  }, [isPharmacy]);

  useEffect(() => {
    if (user) fetchMode();
    else setLoading(false);
  }, [user, fetchMode]);

  const isConnected = mode === 'connected';
  const isManual = mode === 'manual';

  /**
   * Check if a specific field/action is restricted in connected mode.
   * @param {string} field - 'stock' | 'delivery' | 'catalog'
   * @param {string} action - 'write' (default)
   * @returns {boolean}
   */
  const isRestricted = useCallback((field, action = 'write') => {
    if (!isConnected) return false;
    return restrictions.some(r => r.field === field && r.action === action);
  }, [isConnected, restrictions]);

  /**
   * Get the restriction reason message for a field.
   */
  const getRestrictionReason = useCallback((field) => {
    const r = restrictions.find(r => r.field === field);
    return r?.reason || 'This field is managed by the connected company';
  }, [restrictions]);

  const disconnect = useCallback(async (identifier) => {
    const res = await api.post(`/pharmacy/disconnect/${identifier}`);
    await fetchMode(); // refresh
    return res.data;
  }, [fetchMode]);

  return (
    <PharmacyModeContext.Provider value={{
      mode, isConnected, isManual, connections, restrictions,
      primaryCompany, loading, isRestricted, getRestrictionReason,
      disconnect, refresh: fetchMode, isPharmacy,
    }}>
      {children}
    </PharmacyModeContext.Provider>
  );
}

export function usePharmacyMode() {
  const ctx = useContext(PharmacyModeContext);
  if (!ctx) throw new Error('usePharmacyMode must be used within PharmacyModeProvider');
  return ctx;
}
